import { IJackpotData } from "../jackpots-with-state.service.interface";
import { action, observable } from "mobx";
import { IServiceFactoryExtended } from "../../service-factory-extended.interface";
import { IGradualIncreaseHelper } from "../../../../utils/gradual-increase-helper.interface";
import { IJackpotHandler } from "./jackpot-handler.interface";
import { reaction, IReactionDisposer } from 'mobx'

export class JackpotHandler implements IJackpotHandler {
    @observable.ref
    public jackpotData: IJackpotData | null = null;
    /**
     * We don't show in the interface the value directly from this.currentJackpot.amountInCents because most of the time we want
     * to show a gradual increase of the amount. So we pass that value to the gradualIncreaseHelper which will set this value
     * and we show this value instead. This value will briefly be out of sync
     */
    @observable.ref
    public jackpotAmount: number = 0;

    private gradualIncreaseAmountHelper: IGradualIncreaseHelper | undefined;
    private readonly onAuthChangeReaction: IReactionDisposer;
    private previousUpdateTime;
    private gradualRealAmountInCents;
    private isFastGradualIncreaseFinish = false;

    constructor(private services: IServiceFactoryExtended) {
        /**
         * This service must listen to auth changes and when that happens to remove the gradual increase helper. It will be recreated when needed
         */
        this.onAuthChangeReaction = reaction(
            () => this.services.user.isAuthorized,
            () => this.removeGradualIncreaseAmountHelper(),
        );
    }

    @action
    startGradual(jackpot: IJackpotData | null) {
        let newState = this.jackpotData?.jackpotState !== jackpot.jackpotState;
        this.previousUpdateTime = Date.now();

        if (!this.gradualIncreaseAmountHelper || newState) {
            // If new state or not exists - start fast gradual increase.
            this.createGradualIncreaseAmountHelper(50, 3000);
            this.gradualIncreaseAmountHelper.updateAmount(jackpot.amountInCents, this.previousUpdateTime, 0);
            this.gradualRealAmountInCents = jackpot.amountInCents;
        } else if(this.jackpotAmount === this.gradualRealAmountInCents && !this.isFastGradualIncreaseFinish) {
            // After fast gradual increase finish - start slow gradual increase from current value to the new value.
            this.isFastGradualIncreaseFinish = true;
            this.createGradualIncreaseAmountHelper();
            this.gradualIncreaseAmountHelper.updateAmount(jackpot.amountInCents, this.previousUpdateTime, this.jackpotData.amountInCents);
        } else if(this.isFastGradualIncreaseFinish){
            // Update new amount with slow gradual increase from current value to the new value.
            this.gradualIncreaseAmountHelper.updateAmount(jackpot.amountInCents, this.previousUpdateTime, this.jackpotData.amountInCents);
        }
    }

    @action
    public setJackpotData(jackpot: IJackpotData | null, useGradualAmountIncrease: boolean = true) {
        if (jackpot) {
            if(useGradualAmountIncrease) {
                this.startGradual(jackpot);
            }else {
                this.setCurrentJackpotAmount(jackpot.amountInCents);
            }
        }
        this.jackpotData = jackpot;
    }

    @action
    private setCurrentJackpotAmount(val: number) {
        this.jackpotAmount = val;
    }

    private createGradualIncreaseAmountHelper(frequency?: number, interval?:number) {
        this.removeGradualIncreaseAmountHelper();
        this.gradualIncreaseAmountHelper = this.services.gradualIncreaseHelperFactory.create({
            updateAmountFunction: (amount) => this.setCurrentJackpotAmount(amount),
            frequency: frequency || 300,
            interval: interval
        });
    }

    private removeGradualIncreaseAmountHelper(){
        if (this.gradualIncreaseAmountHelper){
            this.gradualIncreaseAmountHelper.restart();
            this.gradualIncreaseAmountHelper = undefined;
        }
    }

    public dispose() {
        this.onAuthChangeReaction();
        this.removeGradualIncreaseAmountHelper();
    }
}
